import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';

import { shutdown } from '@intercom/messenger-js-sdk';

import {
    endPoints,
    postSignInData,
    postForgotPassword,
    postResetPassword,
    getResetPassword,
    postHubspotTokenRequest,
    invitedUserSignupTokenValidation,
    newUserSignupTokenValidation,
    postInvitedUserSignUpData,
    signupUser,
    newUserSignupAddCompanyInfo,
    newUserSignupAddPersonalInfo,
    postSSOIdentifierRequest,
    postSSOAuthenticationRequest,
    logOut,
    handleTwoFactorAuthLoginViaAuthenticator,
    getTwoFactorPreferences,
    generateQRCodeForAuthenticatorSetup,
    verifyOTPForAuthenticatorSetup,
    getRecoveryCodeCount,
    generateRecoveryCodes,
    handleTwoFactorAuthLoginViaRecoveryCode,
    onSSOSetup,
    disableSSOSetup,
    updateTwoFactorAuthPreferences
} from './apis';
import { getUserProfileInfo } from '../../Settings/MyProfile/queries/apis';
import { queryClient } from 'src/app/queryClient';
import { AxiosResponse } from 'axios';
import { NewUserSignUpVerifyInvitationResponse, ResetPasswordMailTokenValidationResponse, SignUpValidateInvitationResponse, TwoFactorPreferencesResponse } from './apiTypes';
import { invalidateUserProfileInfoQuery } from 'src/features/Settings/MyProfile/queries';

const getResetPasswordKey = (token: string) => [endPoints.resetPassword(token)];
const getInvitedUserSignupInvitationValidationKey = (token: string) => [endPoints.validateInvitedUserSignupInvitation(token)];
const getNewUserSignupInvitationValidationKey = (token: string) => [endPoints.validateInvitedUserSignupInvitation(token)];
const authTokenKey = ['auth-token-key'];
const twoFactorPreferencesKey = [endPoints.twoFactorPreferences];
const recoveryCodeKey = [endPoints.recoveryCodeCountUrl];

export function updateAuthTokenData(data: { auth: boolean; token: string | null }) {
    queryClient.setQueriesData(authTokenKey, data);
}

export function deleteAuthTokenData() {
    updateAuthTokenData({ auth: false, token: null });
}

export function useAuthTokenQuery() {
    return useQuery<{ auth: boolean; token: string | null }>(authTokenKey, () => Promise.resolve({ auth: localStorage.getItem('auth') === 'true', token: localStorage.getItem('token') }));
}

export const useSignInUserMutation = () =>
    useMutation(postSignInData, {
        onSuccess: (response, _payload) => {
            queryClient.removeQueries();
            localStorage.removeItem('user_token'); // If user registers through invited user flow, remove the user_token
            if (localStorage.getItem('two_factor_settings')) {
                localStorage.removeItem('two_factor_settings');
            }
            if (localStorage.getItem('nonce')) {
                localStorage.removeItem('nonce');
            }
            if (!response?.data?.is_two_factor_enabled) {
                updateAuthTokenData(response?.data);
            }
        }
    });

export const useForgotPasswordMutation = () => useMutation(postForgotPassword);

export const useResetPasswordMutation = () => useMutation(postResetPassword);

export const useGetSSOIdentifierMutation = () => useMutation(postSSOIdentifierRequest);

export const useSSOAuthenticationMutation = () =>
    useMutation(postSSOAuthenticationRequest, {
        onSuccess: (response) => {
            queryClient.removeQueries();
            updateAuthTokenData(response?.data);
        }
    });

export const useResetPasswordMailTokenValidationQuery = (token: string, options?: Omit<UseQueryOptions<AxiosResponse<ResetPasswordMailTokenValidationResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(getResetPasswordKey(token), () => getResetPassword(token), options);

export const useHubspotTokenRequestMutation = () => useMutation(postHubspotTokenRequest);

export const useGetUserInfoMutation = () => useMutation(getUserProfileInfo);

export const useValidateInvitedUserSignUpInvitationMutation = (token: string, options?: Omit<UseQueryOptions<AxiosResponse<SignUpValidateInvitationResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(getInvitedUserSignupInvitationValidationKey(token), () => invitedUserSignupTokenValidation(token), options);

export const useSignUpInvitedUserMutation = () => useMutation(postInvitedUserSignUpData);

export const useSignUpNewUserMutation = () => useMutation(signupUser);

export const useValidateNewUserSignUpInvitationMutation = (token: string, options?: Omit<UseQueryOptions<AxiosResponse<NewUserSignUpVerifyInvitationResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(getNewUserSignupInvitationValidationKey(token), () => newUserSignupTokenValidation(token), options);

export const useSignUpNewUserCompanyInfoMutation = () => useMutation(newUserSignupAddCompanyInfo);

export const useSignUpNewUserPersonalInfoMutation = () => useMutation(newUserSignupAddPersonalInfo);

export const useLogoutMutation = () => {
    return useMutation(logOut, {
        onSuccess: () => {
            localStorage.clear();
            deleteAuthTokenData();
            queryClient.removeQueries();
            shutdown();
        }
    });
};

const invalidateTwoFactorPreferenceData = () => {
    queryClient.invalidateQueries(twoFactorPreferencesKey);
};

export const useGetTwoFactorPreferencesQuery = (options?: Omit<UseQueryOptions<AxiosResponse<TwoFactorPreferencesResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(twoFactorPreferencesKey, getTwoFactorPreferences, { ...options });

export const useTwoFactorAuthLoginViaAuthenticatorMutation = () =>
    useMutation(handleTwoFactorAuthLoginViaAuthenticator, {
        onSuccess: (response) => {
            queryClient.removeQueries();
            updateAuthTokenData(response?.data);
            localStorage.removeItem('nonce');
            localStorage.removeItem('two_factor_settings');
        }
    });

export const useTwoFactorAuthLoginViaRecoveryCodeMutation = () =>
    useMutation(handleTwoFactorAuthLoginViaRecoveryCode, {
        onSuccess: (response) => {
            queryClient.removeQueries();
            updateAuthTokenData(response?.data);
            localStorage.removeItem('nonce');
            localStorage.removeItem('two_factor_settings');
        }
    });

export const useTwoFactorChangePreferenceMutation = () =>
    useMutation(updateTwoFactorAuthPreferences, {
        onSuccess: () => {
            invalidateTwoFactorPreferenceData();
        }
    });

//For Setup Screens
const invalidateRecoveryCodeCountApi = () => {
    queryClient.invalidateQueries(recoveryCodeKey);
};

export const useGenerateQRCodeForAuthenticatorMutation = () => useMutation(generateQRCodeForAuthenticatorSetup);

export const useVerifyAuthenticatorOtpMutation = () =>
    useMutation(verifyOTPForAuthenticatorSetup, {
        onSuccess: () => {
            invalidateTwoFactorPreferenceData();
            invalidateRecoveryCodeCountApi();
        }
    });

export const useGetRecoveryCodeCountQuery = (options?: object) => useQuery(recoveryCodeKey, getRecoveryCodeCount, { ...options });

export const useGenerateRecoveryCodeMutation = () =>
    useMutation(generateRecoveryCodes, {
        onSuccess: () => {
            invalidateRecoveryCodeCountApi();
        }
    });

export const useSSOSetupMutation = () =>
    useMutation(onSSOSetup, {
        onSuccess: () => {
            invalidateUserProfileInfoQuery();
            invalidateTwoFactorPreferenceData();
        }
    });

export const useDisableSSOSetupMutation = () =>
    useMutation(disableSSOSetup, {
        onSuccess: () => {
            invalidateUserProfileInfoQuery();
        }
    });
